import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { getBridgeLogger } from "../logger.js";
import { fetchProviderModels, getStaticModels, type ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import type { AgentConfig, DriverInfo } from "../types.js";
import { extractCost, extractText, extractTokens, SubprocessCliDriver } from "./_subprocess-cli.js";

/**
 * Shared base for the Gemini-CLI event family. Gemini CLI and Qwen Code (a fork
 * of Gemini CLI) emit the same `stream-json` event shape, so both dispatch the
 * same {@link handleJson}: `init`/`system` → session capture, `message`/
 * `assistant` → streamed assistant text, `tool_use` / `tool_result` → tool
 * events, `result` → tokens + cost.
 *
 * @docLink packages/bridge/drivers#gemini-driver
 */
export abstract class GeminiFamilyDriver extends SubprocessCliDriver {
  protected handleJson(json: any): void {
    switch (json?.type) {
      case "init":
      case "system":
        this.captureSession(json.sessionId ?? json.session_id ?? json.id);
        break;

      case "message":
      case "assistant": {
        const role = json.role ?? json.message?.role ?? "assistant";
        if (role !== "assistant") break;
        const text = extractText(json);
        if (json.done === true || json.final === true || json.finished === true) {
          this.pushAssistantEnd(text);
        } else {
          this.pushAssistantDelta(text);
        }
        break;
      }

      case "tool_use":
      case "tool_call":
        this.pushToolCall(json.name ?? json.tool ?? "tool", json.input ?? json.args ?? {});
        break;

      case "tool_result":
        this.pushToolResult(
          json.name ?? json.tool ?? "tool",
          json.result ?? json.output ?? json.content,
          Boolean(json.is_error ?? json.isError ?? json.error),
        );
        break;

      case "error":
        this.pushError(this.classifyMessage(String(json.message ?? json.error ?? "error")));
        break;

      case "result":
        this.captureSession(json.sessionId ?? json.session_id);
        this.pushResult({
          tokens: extractTokens(json),
          costUsd: extractCost(json),
          summary: extractText(json) || "Task completed",
        });
        break;
    }
  }
}

/**
 * Agent driver for the Google **Gemini CLI** (`gemini`).
 *
 * Spawns `gemini -p "<prompt>" --output-format stream-json` per turn and maps
 * the stream-json events to the unified {@link AgentEvent} stream. Google /
 * Vertex models only — uses `GEMINI_API_KEY` from `apiKeys.google` /
 * `apiKeys.gemini` / env.
 *
 * @remarks
 * - **Requires Gemini CLI ≥ v0.6.1** — earlier versions reject
 *   `--output-format`. A too-old CLI surfaces as a `process`-category exit error.
 * - **Session resume is not available in headless (`-p`) mode** — the CLI errors
 *   if asked to resume non-interactively, so this driver runs single-turn per
 *   process and logs a warning when a resume id is present.
 * - Exit codes: `42` → input/validation error, `53` → turn-limit (treated as a
 *   completed turn).
 *
 * @see https://github.com/google-gemini/gemini-cli
 * @docLink packages/bridge/drivers#gemini-driver
 */
export class GeminiDriver extends GeminiFamilyDriver {
  readonly driverInfo: DriverInfo = {
    id: "gemini",
    name: "Gemini CLI",
    modelAgnostic: false,
    supportsInBandAbort: false,
  };

  protected readonly log: Logger = getBridgeLogger("gemini");
  protected readonly defaultBin = "gemini";
  protected readonly productName = "Gemini CLI";
  protected readonly envPrefix = "GEMINI";

  protected buildArgs(message: string, resumeId: string | undefined): string[] {
    if (resumeId) {
      this.log.warn("Gemini CLI cannot resume in headless mode; starting a fresh turn", {
        resumeId,
      });
    }
    const args = ["-p", message, "--output-format", "stream-json"];
    if (this.config.model) args.push("-m", this.config.model);
    return args;
  }

  protected buildEnv(): Record<string, string> {
    const env = this.baseEnv(["GEMINI_API_KEY", "GOOGLE_API_KEY"]);
    const key =
      this.config.apiKeys?.google ??
      this.config.apiKeys?.gemini ??
      this.config.env?.GEMINI_API_KEY ??
      process.env.GEMINI_API_KEY;
    if (key) env.GEMINI_API_KEY = key;
    return env;
  }

  protected installHint(): string {
    return "Gemini CLI is not installed or not on your PATH. Install it with `npm i -g @google/gemini-cli` (≥ v0.6.1).";
  }

  protected override classifyExit(code: number | null, signal: NodeJS.Signals | null) {
    if (code === 42) {
      return {
        message: "Gemini CLI reported an input error (exit 42)",
        category: "validation" as const,
        retryable: false,
        hint: "The prompt or flags were rejected by Gemini CLI. Check the model name and arguments.",
      };
    }
    if (code === 53) return null; // turn-limit — the result was already emitted.
    return super.classifyExit(code, signal);
  }

  override async listModels(): Promise<ModelEntry[]> {
    const key =
      this.config.apiKeys?.google ?? this.config.apiKeys?.gemini ?? process.env.GEMINI_API_KEY;
    if (!key) return getStaticModels("google");
    const result = await fetchProviderModels("google", key);
    return result.ok && result.models.length > 0 ? result.models : getStaticModels("google");
  }
}

/**
 * `DriverTarget` wrapper for the gemini driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`.
 */
export const geminiDriverTarget: DriverTarget<AgentConfig> = {
  id: "gemini",
  displayName: BUILTIN_DRIVER_CATALOG.gemini?.name ?? "Gemini CLI",
  apiVersion: 1,
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new GeminiDriver(config),
  listModels: async (apiKeys) =>
    new GeminiDriver({ cwd: process.cwd(), sessionId: "model-list", apiKeys }).listModels(),
};
